/**
 * Interpolates display text values into a template string.
 *
 * This utility function replaces placeholder tokens in a template string with
 * corresponding values from a display text object. Placeholders use the format
 * `{{key}}` where `key` matches a property in the display text object.
 *
 * @param template - The template string containing placeholder tokens
 * @param displayText - Object containing key-value pairs for interpolation
 * @returns The template with all placeholders replaced by their values
 *
 * @example
 * ```typescript
 * const template = '<h1>{{title}}</h1><p>{{description}}</p>';
 * const displayText = {
 *   title: 'Welcome',
 *   description: 'This is a welcome message'
 * };
 *
 * const result = projectDisplayText(template, displayText);
 * // Result: '<h1>Welcome</h1><p>This is a welcome message</p>'
 * ```
 *
 * @example
 * ```typescript
 * // Missing keys are left as-is
 * const template = '{{greeting}} {{name}}! {{missing}}';
 * const displayText = { greeting: 'Hello', name: 'World' };
 *
 * const result = projectDisplayText(template, displayText);
 * // Result: 'Hello World! {{missing}}'
 * ```
 */
export function projectDisplayText(
  template: string,
  displayText: Record<string, string>
) {
  return template.replace(
    /{{(.*?)}}/g,
    (match, key: string) => displayText[key] ?? match
  );
}
